import * as AdvertType from "enums/AdvertType"
import * as BoatSaleType from "enums/BoatSaleType"
import * as BoatCoOwnershipType from "enums/BoatCoOwnershipType"
import * as BudgetOwner from "enums/BudgetOwner"
import { AdvertRoute } from "domains/advert/AdvertRoute"
import { Button } from "components/Button"
import { Tag } from "components/Tag"
import { Text } from "components/Text"
import { Section } from "components/Section"
import { Fees, FeesRow } from "components/Fees"
import { CalendarCheckRegular } from "components/Icon/CalendarCheckRegular"
import { useTranslator } from "components/Translator"
import { BoatHeader } from "domains/boat/BoatHeader"
import { BoatImages } from "domains/boat/BoatImages"
import { BoatGeneral } from "domains/boat/BoatGeneral"
import { BoatDescription } from "domains/boat/BoatDescription"
import { BoatComfort } from "domains/boat/BoatComfort"
import { BoatEnergy } from "domains/boat/BoatEnergy"
import { BoatSafety } from "domains/boat/BoatSafety"
import { Price } from "components/Price"
import { MapboxProvider } from "components/Map"
import { AdvertMapModal } from "domains/advert/AdvertMapModal"
import { ProfilePage } from "components/ProfilePage"
import { BudgetSection } from "components/BudgetSection"
import { AdvertParticipants } from "domains/advert/AdvertParticipants"
import {
  Accordion,
  AccordionSummary,
  AccordionContent,
} from "components/Accordion"
import { Heart } from "components/Icon/Heart"
import { HeartRegular } from "components/Icon/HeartRegular"
import { ShareAlt } from "components/Icon/ShareAlt"
import { RequireLogin } from "components/RequireLogin"
import {
  AdvertInteractionsProvider,
  useAdvertInteractions,
} from "domains/advert/AdvertInteractionsProvider"
import { ShareModal } from "components/ShareModal"
import { Separator } from "components/Separator"
import { AdvertTags } from "domains/advert/AdvertTags"
import * as React from "react"
import cx from "classnames"
import { Description } from "components/Description"
import * as BookingStatus from "enums/BookingStatus"
import { PublicityConfigProvider } from "components/Publicity"
import { getPublicityConfigForAdvertType } from "components/Publicity/config"
import { BoatPosition } from "domains/boat/BoatPosition"

export function AccountAdvertShowPage(props) {
  const { advert, user, tabs = window.accountNav, infos, bookings } = props
  const translator = useTranslator()

  const validatedBookings = bookings
    ? bookings.filter(
        (booking) => booking.status.enum_value === BookingStatus.VALIDATED,
      )
    : []

  return (
    <PublicityConfigProvider
      config={getPublicityConfigForAdvertType(advert.type.enum_value)}
    >
      <ProfilePage user={user} tabs={tabs} infos={infos} advert={advert}>
        <div>
          <Section>
            <div className="space-y-2">
              <AdvertInteractionsProvider ids={[advert.id]}>
                <RequireLogin>
                  <AdvertHeader advert={advert} />
                </RequireLogin>
              </AdvertInteractionsProvider>
              {advert.boat ? <BoatImages boat={advert.boat} /> : null}
              <div className="flex flex-col space-y-10">
                <div className="flex flex-col space-y-4">
                  {advert.title ? (
                    <Text variant="headline3" tag="h1">
                      {advert.title}
                    </Text>
                  ) : null}
                </div>

                <AdvertTags advert={advert} />

                <AdvertRoute advert={advert} variant="full" />
                {advert.departureType ||
                advert.arrivalType ||
                advert.stopovers.length > 0 ? (
                  <div className="flex justify-center pt-2">
                    <MapboxProvider>
                      <AdvertMapModal
                        advert={advert}
                        trigger={
                          <Button variant="outline">
                            {translator.trans(
                              "account.advert.show.showMap",
                              null,
                              "pages",
                            )}
                          </Button>
                        }
                      />
                    </MapboxProvider>
                  </div>
                ) : null}

                {advert.event ? (
                  <div>
                    <Tag
                      iconStart={
                        <CalendarCheckRegular className="w-3 text-primary-default" />
                      }
                      color="notice"
                      className="flex whitespace-nowrap"
                      label={advert.event.name}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </Section>
          <Separator />
          {advert.description ? (
            <Section>
              <Section.Title>
                {translator.trans(
                  "account.advert.show.description",
                  null,
                  "pages",
                )}
              </Section.Title>

              <div className="bg-white px-5 py-4">
                <Description>{advert.description}</Description>
              </div>
              <Separator />
            </Section>
          ) : null}
          <Separator />
          <BudgetSection entity={advert} />
          {advert.type.enum_value === AdvertType.BOAT &&
          advert.budgetOwner?.choice?.enum_value === BudgetOwner.SHARE_COSTS &&
          (advert.budgetOwner.sharingPot?.amount !== null ||
            advert.budgetOwner.sharingMaintenance?.amount !== null) ? (
            <div>
              <Fees
                title={translator.trans(
                  "account.advert.show.feesTitle",
                  null,
                  "pages",
                )}
              >
                {advert.budgetOwner.sharingMaintenance !== null ? (
                  <FeesRow
                    label={translator.trans(
                      "account.advert.show.boardingFees",
                      null,
                      "pages",
                    )}
                    amount={advert.budgetOwner.sharingMaintenance.amount}
                    currency={
                      advert.budgetOwner.sharingMaintenance.currency.enum_value
                    }
                    unit={translator.trans(
                      "account.advert.show.unitManDay",
                      null,
                      "pages",
                    )}
                  />
                ) : null}
                {advert.budgetOwner.sharingPot !== null ? (
                  <FeesRow
                    label={translator.trans(
                      "account.advert.show.sharingFees",
                      null,
                      "pages",
                    )}
                    amount={advert.budgetOwner.sharingPot.amount}
                    currency={advert.budgetOwner.sharingPot.currency.enum_value}
                    unit={translator.trans(
                      "account.advert.show.unitManDay",
                      null,
                      "pages",
                    )}
                  />
                ) : null}
              </Fees>
            </div>
          ) : null}
          {advert.type.enum_value === AdvertType.BOAT &&
          advert.budgetOwner?.choice?.enum_value === BudgetOwner.PRO_ORG ? (
            <Fees
              title={translator.trans(
                "account.advert.show.feesTitle",
                null,
                "pages",
              )}
            >
              {advert.budgetOwner.pro.price?.amount &&
              advert.budgetOwner.pro.priceMeasure &&
              advert.budgetOwner.pro.priceFrequency ? (
                <FeesRow
                  label={translator.trans(
                    advert.budgetOwner.pro.pot?.amount
                      ? "account.advert.show.boardingBox"
                      : "account.advert.show.boardingBoxIncluded",
                    null,
                    "pages",
                  )}
                  amount={advert.budgetOwner.pro.price.amount}
                  currency={advert.budgetOwner.pro.price.currency.enum_value}
                  unit={`/ ${advert.budgetOwner.pro.priceFrequency.enum_label} / ${advert.budgetOwner.pro.priceMeasure.enum_label}`}
                />
              ) : null}
              {advert.budgetOwner.pro.pot?.amount ? (
                <FeesRow
                  label={translator.trans(
                    "account.advert.show.sharingFees",
                    null,
                    "pages",
                  )}
                  amount={advert.budgetOwner.pro.pot.amount}
                  currency={advert.budgetOwner.pro.pot.currency.enum_value}
                  unit={translator.trans(
                    "account.advert.show.unitManDay",
                    null,
                    "pages",
                  )}
                />
              ) : null}
            </Fees>
          ) : null}
          {advert.type.enum_value === AdvertType.RENTAL ? (
            <Section>
              <Section.Title>
                {translator.trans(
                  "account.advert.show.rentalPeriodsInfosSectionTitle",
                  null,
                  "pages",
                )}
              </Section.Title>
              {advert.rentalPeriods.length > 0 ? (
                <div className="flex flex-col space-y-4">
                  <Text variant="headline5">
                    {translator.trans(
                      "account.advert.show.rentalPeriodsSectionTitle",
                      null,
                      "pages",
                    )}
                  </Text>
                  {advert.rentalPeriods.map((rentalPeriod) => {
                    return (
                      <Fees
                        key={rentalPeriod.id}
                        title={translator.trans(
                          "account.advert.show.dateInterval",
                          {
                            from: new Date(
                              rentalPeriod.dateInterval.from,
                            ).toLocaleDateString(translator.locale),
                            to: new Date(
                              rentalPeriod.dateInterval.to,
                            ).toLocaleDateString(translator.locale),
                          },
                          "pages",
                        )}
                      >
                        {rentalPeriod.priceDay &&
                        rentalPeriod.priceDay?.amount > 0 ? (
                          <FeesRow
                            label={translator.trans(
                              "account.advert.show.priceDay",
                              null,
                              "pages",
                            )}
                            amount={rentalPeriod.priceDay.amount}
                            currency={rentalPeriod.priceDay.currency.enum_value}
                          />
                        ) : null}
                        {rentalPeriod.priceWeek &&
                        rentalPeriod.priceWeek?.amount > 0 ? (
                          <FeesRow
                            label={translator.trans(
                              "account.advert.show.priceWeek",
                              null,
                              "pages",
                            )}
                            amount={rentalPeriod.priceWeek.amount}
                            currency={
                              rentalPeriod.priceWeek.currency.enum_value
                            }
                          />
                        ) : null}
                        {rentalPeriod.priceWeekEnd &&
                        rentalPeriod.priceWeekEnd?.amount > 0 ? (
                          <FeesRow
                            label={translator.trans(
                              "account.advert.show.priceWeekEnd",
                              null,
                              "pages",
                            )}
                            amount={rentalPeriod.priceWeekEnd.amount}
                            currency={
                              rentalPeriod.priceWeekEnd.currency.enum_value
                            }
                          />
                        ) : null}
                      </Fees>
                    )
                  })}
                </div>
              ) : null}
            </Section>
          ) : null}
          {advert.type.enum_value === AdvertType.SALE ? (
            <Section>
              <Section.Title>
                {translator.trans(
                  "account.advert.show.salesInfosTitle",
                  null,
                  "pages",
                )}
              </Section.Title>
              {advert.saleType &&
              advert.saleType.enum_value === BoatSaleType.SALE ? (
                <ShowSaleAmount
                  label={translator.trans(
                    "account.advert.show.saleAmountSale",
                    null,
                    "pages",
                  )}
                  advert={advert}
                />
              ) : (
                <ShowSaleAmount advert={advert} />
              )}
            </Section>
          ) : null}
          <div className="py-7">
            <Separator />
          </div>
          {[AdvertType.BOAT, AdvertType.FLEET].includes(
            advert.type.enum_value,
          ) ? (
            <React.Fragment>
              <Accordion defaultExpanded={validatedBookings.length === 0}>
                <AccordionSummary disabled={validatedBookings.length === 0}>
                  {translator.trans(
                    "account.advert.show.participants",
                    null,
                    "pages",
                  )}
                </AccordionSummary>
                <AccordionContent>
                  {(expanded) => (
                    <AdvertParticipants
                      advert={advert}
                      bookings={bookings}
                      variant={expanded ? "full" : "compact"}
                    />
                  )}
                </AccordionContent>
              </Accordion>

              <div className="py-7">
                <Separator />
              </div>
            </React.Fragment>
          ) : null}

          {advert.boat ? (
            <Section>
              <BoatHeader boat={advert.boat} />
              <BoatGeneral boat={advert.boat} />
              <BoatPosition boat={advert.boat} />
              <BoatDescription boat={advert.boat} />
              <div className="space-y-8 lg:space-y-10">
                <BoatEnergy boat={advert.boat} />
                <BoatComfort boat={advert.boat} />
                <BoatSafety boat={advert.boat} />
              </div>
            </Section>
          ) : null}
        </div>
      </ProfilePage>
    </PublicityConfigProvider>
  )
}

function SaleAmountLabel(props) {
  const { advert } = props

  const isCoOwnership =
    advert.saleType?.enum_value === BoatSaleType.CO_OWNERSHIP
  const isBecomeCoOwnershipType =
    advert.coOwnershipType?.enum_value === BoatCoOwnershipType.BECOME
  const isLookForCoOwnershipType =
    advert.coOwnershipType?.enum_value === BoatCoOwnershipType.LOOK_FOR
  const isSellSharesType =
    advert.coOwnershipType?.enum_value === BoatCoOwnershipType.SELL_SHARES

  const isBecomeOrLookFor = isBecomeCoOwnershipType || isLookForCoOwnershipType
  const translator = useTranslator()

  if (isCoOwnership && isBecomeOrLookFor) {
    return translator.trans(
      "account.advert.show.saleAmountLookFor",
      null,
      "pages",
    )
  }

  if (isLookForCoOwnershipType) {
    return translator.trans(
      "account.advert.show.saleAmountLookFor",
      null,
      "pages",
    )
  }

  if (isSellSharesType) {
    return translator.trans(
      "account.advert.show.saleAmountSellShares",
      null,
      "pages",
    )
  }
  return null
}

function ShowSaleAmount(props) {
  const { advert, label } = props

  const labelValue = label ? label : <SaleAmountLabel advert={advert} />

  return (
    <div className="flex flex-row space-x-2">
      <Text variant="headline5" className="text-primary-dark">
        {labelValue}
      </Text>
      <div>
        <Price
          amount={advert.salePrice.amount}
          currency={advert.salePrice.currency.enum_value}
        />
      </div>
    </div>
  )
}

function AdvertHeader(props) {
  const { advert } = props
  const translator = useTranslator()
  const advertInteractions = useAdvertInteractions()
  const [showShareModal, setShowShareModal] = React.useState(false)
  const interactionsData = advertInteractions.get(advert.id)
  const HeartIcon = interactionsData?.favorite ? Heart : HeartRegular

  let urlWithoutFrom = new URL(window.location.toString())
  urlWithoutFrom.searchParams.delete("from")
  const url = urlWithoutFrom.toString()

  const subject = encodeURIComponent(
    translator.trans("ShareModal.byMail.subject", null, "components"),
  )
  const body = translator.trans(
    "ShareModal.byMail.body",
    { url: encodeURIComponent(url) },
    "components",
  )

  const handleLikeClick = () => {
    advertInteractions.toggleFavorite(advert.id)
  }

  const handleShareClick = () => {
    setShowShareModal(true)
  }

  return (
    <div className="flex flex-row items-center justify-between">
      <div className="flex flex-row space-x-2 items-center">
        <Text variant="caption">#{advert.id}</Text>
        {advert.updatedAt ? (
          <Text className="mr-auto" variant="caption">
            {new Date(advert.updatedAt).toLocaleString(translator.locale)}
          </Text>
        ) : null}
        {advert.nbVisits ? (
          <Text className="mr-auto" variant="caption">
            {translator.trans(
              "account.advert.show.nbVisits",
              { nbVisits: advert.nbVisits },
              "pages",
            )}
          </Text>
        ) : null}
        {[AdvertType.BOAT, AdvertType.FLEET].includes(advert.type.enum_value) &&
        advert.completed ? (
          <Tag
            color="error"
            label={translator.trans("SearchResult.full", null, "components")}
          />
        ) : null}
      </div>
      <div className="flex flex-row space-x-2">
        <button
          type="button"
          className="flex ml-auto p-2 bg-white items-center rounded-full"
          onClick={handleLikeClick}
        >
          <HeartIcon className={cx("w-6 text-primary-dark")} />
        </button>
        <button
          type="button"
          className="flex ml-auto p-2 bg-white rounded-full"
          onClick={handleShareClick}
        >
          <ShareAlt className={cx("w-6 text-primary-dark")} />
        </button>
      </div>
      <ShareModal
        isOpen={showShareModal}
        onRequestClose={() => setShowShareModal(false)}
        title={translator.trans("ShareModal.title", null, "components")}
        url={url}
        email={{ subject: subject, body: body }}
      />
    </div>
  )
}
